import Honeybadger from 'honeybadger-js';
import React from 'react';

import {
  QuestionItemsFragment,
  useCreateQuestionItemMutation,
  useDeleteQuestionItemMutation,
  useQuestionQuery,
  useUpdateQuestionItemMutation,
} from '../../../generated/graphql';
import addItemToPage from '../../../helpers/apolloCache/addItemToPage';
import deletePageItem from '../../../helpers/apolloCache/deletePageItem';
import { useManageItem } from '../../../helpers/useManageItem';
import Layout from '../../FormComponents/Layout';
import { pagesQuery } from '../../LeftContainer/graphql';
import { PageItemProps } from '../BasicItem';
import Form, { InitialValues, initialValues, validationSchema } from './Form';

export const QuestionItem: React.FC<PageItemProps> = ({ onSaveHandler }) => {
  const { pageId, itemId, isNew, close } = useManageItem();

  const [update, { loading: updating }] = useUpdateQuestionItemMutation();
  const [create, { loading: creating }] = useCreateQuestionItemMutation();
  const [destroy, { loading: deleting }] = useDeleteQuestionItemMutation();

  const { data, loading } = useQuestionQuery({
    variables: {
      pageId,
      id: itemId!,
    },
    skip: isNew,
  });

  const item = data?.page?.questionItem;

  const save = async (values: InitialValues): Promise<void> => {
    try {
      if (item?.id) {
        await update({
          variables: {
            attributes: {
              id: item.id,
              pageId: pageId,
              question: values.question || '',
              answer: values.answer || '',
            },
          },
          update: (_cache, { data }) => close(data?.updateQuestionItem),
        });
      } else {
        await create({
          variables: {
            attributes: {
              pageId,
              question: values.question || '',
              answer: values.answer || '',
            },
          },
          update: (cache, { data }) => {
            addItemToPage<QuestionItemsFragment | undefined>(cache, pageId, data?.createQuestionItem, close);
          },
          refetchQueries: [{ query: pagesQuery }],
        });
      }
      if (onSaveHandler) {
        onSaveHandler();
      }
    } catch (error) {
      Honeybadger.notify(error, {
        message: `QuestionItemForm: ${error.message}`,
        context: {
          error,
        },
      });
    }
  };

  const onDelete = async () => {
    if (itemId) {
      try {
        await destroy({
          variables: {
            attributes: {
              id: itemId,
              pageId,
            },
          },
          update(cache) {
            deletePageItem({ cache, pageId, itemId });
          },
          refetchQueries: [{ query: pagesQuery }],
        });
      } catch (error) {
        Honeybadger.notify(error, {
          message: 'Error deleting question item',
          context: {
            error,
          },
        });
      }
    }
  };

  return (
    <Layout
      headerText={`${isNew ? 'Add a' : 'Update'} Question`}
      validationSchema={validationSchema}
      initialValues={initialValues(item)}
      onSubmit={save}
      Component={Form}
      loading={loading}
      updating={updating || creating || deleting}
      close={close}
      pageId={pageId}
      isNew={isNew}
      onDelete={onDelete}
    />
  );
};
